import { cva } from "class-variance-authority";

export const labelCva = cva("text-theme-light-gray-2 dark:text-white gap-1 text-sm mb-1", {
  variants: {
    labelPosition: {
      left: "",
      top: "block",
    },
    layout: {
      "is-row": "",
      "is-column": "",
    },
  },
  compoundVariants: [
    {
      labelPosition: "left",
      layout: "is-row",
      className: "inline-flex w-1/3",
    },
    {
      labelPosition: "left",
      layout: "is-column",
      className: "inline-flex",
    },
  ],
  defaultVariants: {
    labelPosition: "left",
    layout: "is-column",
  },
});

// Clips the sliding indicator's spring so its overshoot cannot leave the track.
export const buttonContainerCva = cva("relative overflow-hidden p-0.5 bg-white dark:bg-dark-800", {
  variants: {
    disabled: {
      true: ["opacity-50", "cursor-not-allowed"],
      false: null,
    },
    loading: {
      true: ["opacity-50", "cursor-wait", "animate-pulse"],
      false: null,
    },
    invalid: {
      // Only the border turns red: the track stays white, like an invalid input.
      true: ["border border-danger-600"],
      false: ["border border-gray-300 dark:border-dark-600"],
    },
    wrap: {
      true: ["flex flex-wrap gap-0.5 rounded-2xl"],
      false: ["inline-flex rounded-full"],
    },
    block: {
      true: ["grid! w-full auto-cols-fr grid-flow-col"],
      false: null,
    },
  },
  defaultVariants: {
    disabled: false,
    invalid: false,
    loading: false,
    wrap: false,
    block: false,
  },
});

/** The chips appearance has no track: every option is its own pill, and the pills wrap. */
export const chipsContainerCva = cva("flex flex-wrap gap-2", {
  variants: {
    disabled: {
      true: ["opacity-50", "cursor-not-allowed"],
      false: null,
    },
    loading: {
      true: ["opacity-50", "cursor-wait", "animate-pulse"],
      false: null,
    },
  },
  defaultVariants: {
    disabled: false,
    loading: false,
  },
});

export const chipCva = cva(
  [
    "relative inline-flex h-10 items-center gap-2 rounded-full border pl-3 pr-4 text-sm leading-5 tracking-[0.02em]",
    "transition-[color,background-color,border-color] duration-150 ease-out motion-reduce:transition-none",
    "focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500",
  ],
  {
    variants: {
      selected: {
        true: "border-primary-600 bg-primary-50 font-medium text-primary-700 dark:border-primary-400 dark:bg-primary-900/30 dark:text-primary-300",
        false: "border-gray-300 bg-(--input-background) font-normal text-gray-800 hover:bg-gray-50 dark:border-dark-600 dark:text-gray-200 dark:hover:bg-dark-700",
      },
      invalid: {
        true: null,
        false: null,
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
    },
    compoundVariants: [
      { selected: false, invalid: true, className: "border-danger-600 dark:border-danger-600" },
    ],
    defaultVariants: {
      selected: false,
      invalid: false,
      disabled: false,
    },
  },
);

/** The 18px icon of a chip; grey until the chip is selected, then it takes the chip's primary text colour. */
export const chipIconCva = cva("shrink-0 text-[18px]", {
  variants: {
    selected: {
      true: null,
      false: "text-gray-600 dark:text-gray-400",
    },
  },
  defaultVariants: {
    selected: false,
  },
});

/** The option icon of the segmented track grows with the control. */
export const optionIconCva = cva("shrink-0", {
  variants: {
    size: {
      small: "text-xs",
      medium: "text-xs",
      large: "text-[18px]",
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

export const buttonCva = cva(
  [
    "relative z-1 flex items-center gap-1.5 px-3 py-0.5 rounded-full text-sm font-medium",
    "transition-[color,background-color] duration-280 ease-out motion-reduce:transition-none",
    "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500",
  ],
  {
    variants: {
      selected: {
        true: null,
        false: ["text-gray-600 dark:text-gray-400 hover:bg-gray-50 hover:text-gray-700 dark:hover:bg-dark-700 dark:hover:text-gray-200"],
      },
      selectedStyle: {
        soft: null,
        solid: null,
      },
      indicator: {
        true: null,
        false: null,
      },
      disabled: {
        true: ["opacity-50", "cursor-not-allowed"],
        false: null,
      },
      block: {
        true: ["w-full justify-center"],
        false: null,
      },
      // Track height = option min-height + 2px padding on each side + 1px border on each side (6px), so the
      // control is as tall as the field of the same size: 28px (VButton small), 32px (VInput medium), 40px (VInput large).
      size: {
        small: "min-h-[22px]",
        medium: "min-h-[26px]",
        large: "min-h-[34px]",
      },
    },
    compoundVariants: [
      {
        selected: true,
        selectedStyle: "soft",
        className: "text-primary-700 dark:text-primary-400",
      },
      {
        selected: true,
        selectedStyle: "solid",
        className: "text-white",
      },
      {
        selected: true,
        selectedStyle: "soft",
        indicator: false,
        className: "bg-primary-50 dark:bg-primary-900/30",
      },
      {
        selected: true,
        selectedStyle: "solid",
        indicator: false,
        className: "bg-primary-600",
      },
    ],
    defaultVariants: {
      selected: false,
      selectedStyle: "soft",
      indicator: false,
      disabled: false,
      block: false,
      size: "medium",
    },
  },
);

/** Label and count side by side, centred on each other. */
export const optionContentCva = cva("inline-flex items-center gap-1.5 leading-5");

// Line boxes centre on the font's ascent and descent, not on the glyphs, so a 12px count next to a 14px label lands a
// fraction of a pixel off depending on the font that renders. Trimming both boxes to cap height and baseline makes
// `items-center` centre the digits on the capitals of the label in every size and state. Browsers without text-box
// keep the line-box centring.
const trimToCapitals = "[text-box:trim-both_cap_alphabetic]";

export const optionLabelCva = cva(trimToCapitals);

export const optionCountCva = cva(`text-xs leading-none font-normal tabular-nums ${trimToCapitals}`, {
  variants: {
    selected: {
      true: "text-primary-600 dark:text-primary-400",
      false: "text-gray-500 dark:text-gray-400",
    },
  },
  defaultVariants: {
    selected: false,
  },
});

/** Wrapper the sliding indicator is measured into; the surface carries the selected chrome. */
export const indicatorCva = cva("pointer-events-none absolute left-0 top-0 z-0");

export const indicatorSurfaceCva = cva("block size-full rounded-full", {
  variants: {
    selectedStyle: {
      soft: "bg-primary-50 dark:bg-primary-900/30",
      solid: "bg-primary-600",
    },
  },
  defaultVariants: {
    selectedStyle: "soft",
  },
});

export const requiredCva = cva("text-red-500", {
  variants: {
    required: {
      true: ["font-bold"],
      false: ["hidden"],
    },
  },
  defaultVariants: {
    required: false,
  },
});

export const containerCva = cva("", {
  variants: {
    labelPosition: {
      left: ["inline-block"],
      top: ["flex flex-col"],
    },
    block: {
      true: ["w-full"],
      false: null,
    },
  },
  defaultVariants: {
    labelPosition: "left",
    block: false,
  },
});
